/**
 * As ETIQUETAS de um caso — pôr, tirar, criar, renomear e apagar, num lugar só.
 *
 * O escritório recorta a mesa por etapa (onde o caso está), tese (do que trata)
 * e responsável (de quem é). A etiqueta é o recorte que muda toda semana —
 * "urgente", "audiência marcada", "indicação do Dr. Paulo" — e que antes disso
 * virava nota na trilha, onde não filtra nem conta.
 *
 * Por que ela SAIU do cabeçalho da conversa: lá era uma fita de fichas dentro da
 * linha de apoio da identidade, disputando espaço com telefone e presença, e o
 * catálogo do escritório só existia dentro de um `Combobox` de 210px — a lista
 * onde se renomeia e se apaga a etiqueta de TODOS os casos aparecia num painel
 * que fecha ao perder o foco, com dois ícones de 24px por linha. Gerenciar o
 * vocabulário do escritório num popover é a definição de esconder.
 *
 * Por que carrega o ID no endereço (`?etiquetas=12`) e não `=1`: ela abre também
 * pelo menu de contexto de uma LINHA da lista, que quase nunca é a conversa
 * aberta. Etiquetar varrendo a mesa, sem abrir caso nenhum, é o gesto de quem
 * organiza o dia — e com `=1` ela etiquetaria o caso errado, calada.
 *
 * O que é do CASO e o que é do ESCRITÓRIO ficam visivelmente separados, porque
 * as consequências são de tamanhos diferentes: tirar daqui desfaz-se num toque;
 * renomear e apagar mexem em todos os casos e perguntam antes.
 */
import { useState } from "react";
import * as api from "../api.js";
import { useNavegacao } from "../navegacao.jsx";
import { ICONES, Icone, TINTAS_POR_NOME, tinta } from "../visual.jsx";
import { chaveDaEtiqueta } from "../etiquetas.js";
import { useEtiquetasDoCaso } from "../componentes/EtiquetasDoCaso.jsx";
import { Botao, Cortina, MenuDeAcoes, Vazio } from "../componentes/comuns.jsx";
import { Input } from "@/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";

/**
 * O PINCEL da etiqueta: as cinco cores da paleta, mais a automática.
 *
 * Cinco amostras e não um seletor de cor: a paleta da mesa é fechada
 * (`TINTAS_POR_NOME`, em visual.jsx, sobre os tokens do `index.css`), e um
 * `<input type="color">` deixaria o escritório pôr um roxo neon que o escuro não
 * sabe pintar. A automática (a estável por texto) é a primeira porque é o padrão
 * e é como se DESFAZ a escolha — cor escolhida sem volta é a linha que fica
 * errada para sempre.
 *
 * A cor é do RÓTULO, não do caso: a legenda diz isso, porque pintar aqui muda a
 * etiqueta em todos os casos que a têm.
 */
function Pincel({ tag, cor, aoPintar, ocupado }) {
  const [aberto, setAberto] = useState(false);
  /* Só a LETRA: a amostra é o disco cheio da cor, e o fundo de 14% seria um
     ponto quase branco no lugar de uma cor escolhível. */
  const [, letra] = tinta(tag, cor);
  const escolher = (nome) => {
    setAberto(false);
    aoPintar(nome);
  };
  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={ocupado}
            aria-label={`Cor da etiqueta ${tag}${cor ? `: ${cor}` : ": automática"}`}
            data-dica="Cor desta etiqueta, em todos os casos"
            className="alvo flex size-[26px] flex-none cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 disabled:cursor-wait disabled:opacity-50"
          />
        }
      >
        <span className="size-[15px] rounded-full border border-foreground/20" style={{ background: letra }} />
      </PopoverTrigger>
      {/* `z-[120]`: painel de portal nasce acima da caixa que o abriu, e esta
          mora dentro do modal das etiquetas (que sobe para 51). */}
      <PopoverContent
        align="end"
        sideOffset={6}
        className="z-[120] w-auto rounded-xl border border-foreground/12 bg-card p-2.5 shadow-[0_14px_36px_color-mix(in_oklab,_var(--color-foreground)_16%,_transparent)]"
      >
        <span className="mb-2 block text-selo font-bold tracking-[.08em] text-muted-foreground uppercase">
          Cor em todos os casos
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => escolher("")}
            aria-pressed={!cor}
            aria-label="Cor automática"
            data-dica="Automática — a cor estável pelo nome"
            className={`alvo flex size-[26px] flex-none cursor-pointer items-center justify-center rounded-full border bg-card ${
              !cor ? "border-foreground ring-2 ring-foreground/35" : "border-foreground/20"
            }`}
          >
            <Icone de={ICONES.brilho} tamanho={13} largura={2} />
          </button>
          {Object.entries(TINTAS_POR_NOME).map(([nome, [, letraDela]]) => (
            <button
              key={nome}
              type="button"
              onClick={() => escolher(nome)}
              aria-pressed={cor === nome}
              aria-label={`Pintar de ${nome}`}
              data-dica={nome}
              className={`alvo size-[26px] flex-none cursor-pointer rounded-full border ${
                cor === nome ? "border-foreground ring-2 ring-foreground/35" : "border-foreground/15"
              }`}
              style={{ background: letraDela }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Uma etiqueta DESTE caso, do tamanho de quem se lê e se tira com o dedo. */
function NoCaso({ tag, cor, aoTirar, aoFiltrar }) {
  const [fundo, letra] = tinta(tag, cor);
  return (
    <span
      className="flex flex-none items-center gap-1 rounded-lg py-[4px] pr-[5px] pl-[10px] text-corpo font-semibold"
      style={{ background: fundo, color: letra }}
    >
      <button
        type="button"
        onClick={aoFiltrar}
        data-dica={`Ver todos os casos com “${tag}”`}
        className="cursor-pointer border-none bg-transparent p-0 text-inherit"
      >
        {tag}
      </button>
      {aoTirar ? (
        <button
          type="button"
          onClick={aoTirar}
          aria-label={`Tirar a etiqueta ${tag} deste atendimento`}
          data-dica="Tirar deste atendimento"
          /* `alvo`: o ✕ tem 18px por desenho e o dedo pede 40. */
          className="alvo flex size-[18px] cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-inherit opacity-60 hover:opacity-100"
        >
          <Icone de={ICONES.fechar} tamanho={11} largura={2.4} />
        </button>
      ) : null}
    </span>
  );
}

/**
 * Uma linha do catálogo do escritório.
 *
 * A BARRA de uso é o que a fita de fichas não tinha como dizer: "urgente" em 40
 * casos e "indicação" em 2 são coisas de peso diferente, e quem organiza o
 * vocabulário decide olhando isso. É o mesmo idioma da barra de volume das
 * etapas, no detalhe do filtro da mesa.
 */
function DoEscritorio({
  tag,
  cor,
  n,
  maior,
  tem,
  ocupado,
  aoAlternar,
  aoFiltrar,
  aoRenomear,
  aoApagar,
  aoPintar,
  podeGerenciar,
}) {
  const [fundo, letra] = tinta(tag, cor);
  /* Sem permissão de editar a linha NÃO é botão: um `<button>` que não faz nada
     é foco que o teclado alcança e o leitor de tela anuncia como acionável. */
  const Linha = aoAlternar ? "button" : "div";
  return (
    <div className="flex items-center gap-2.5 border-t border-t-border py-[9px] first:border-t-0">
      <Linha
        {...(aoAlternar
          ? {
              type: "button",
              onClick: aoAlternar,
              disabled: ocupado,
              "aria-pressed": tem,
              "data-dica": tem ? "Tirar deste atendimento" : "Pôr neste atendimento",
            }
          : {})}
        /* O alvo é a LINHA e não uma caixinha: pôr e tirar é o gesto repetido
           desta tela, e caixa de 14px é onde o dedo erra para a linha vizinha. */
        className={`flex min-w-0 flex-1 items-center gap-2.5 border-none bg-transparent p-0 text-left ${
          aoAlternar ? "alvo cursor-pointer disabled:cursor-wait disabled:opacity-50" : ""
        }`}
      >
        {/* O visto ocupa o lugar mesmo vazio: sem isso a linha inteira andava
            2px para o lado a cada toque, e uma lista que pula sob o dedo faz
            errar a próxima. */}
        <span
          className={`flex size-[18px] flex-none items-center justify-center rounded-md border ${
            tem ? "border-transparent bg-verde text-creme" : "border-foreground/20 bg-transparent"
          }`}
        >
          {tem ? <Icone de={ICONES.certo} tamanho={12} largura={2.6} /> : null}
        </span>
        <span
          className="max-w-[45%] flex-none truncate rounded-sm px-[7px] py-[2.5px] text-selo font-semibold"
          style={{ background: fundo, color: letra }}
        >
          {tag}
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {/* A barra é do TAMANHO relativo à etiqueta mais usada: número absoluto
              numa coluna estreita não se compara de relance. */}
          <span className="h-[5px] min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/8">
            <span
              className="block h-full rounded-full bg-foreground/28"
              style={{ width: `${Math.max(4, Math.round((n / Math.max(1, maior)) * 100))}%` }}
            />
          </span>
          <span className="flex-none font-mono text-selo text-muted-foreground">{n}</span>
        </span>
      </Linha>

      {aoPintar ? <Pincel tag={tag} cor={cor} aoPintar={aoPintar} ocupado={ocupado} /> : null}

      <MenuDeAcoes
        rotulo={`O que fazer com a etiqueta ${tag}`}
        titulo="Renomear, apagar ou filtrar por esta etiqueta"
        legenda={tag}
        ancora="bottom end"
        largura="252px"
        classe="flex size-[26px] flex-none items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
        itens={[
          {
            chave: "filtrar",
            rotulo: `Ver os ${n} caso(s) com esta`,
            icone: ICONES.busca,
            aoTocar: aoFiltrar,
          },
          ...(podeGerenciar
            ? [
                {
                  chave: "renomear",
                  rotulo: "Renomear em todos os casos",
                  detalhe: `O rótulo muda nos ${n} caso(s) que o têm.`,
                  icone: ICONES.editar,
                  aoTocar: aoRenomear,
                },
                {
                  chave: "apagar",
                  rotulo: "Apagar do escritório",
                  detalhe: "Sai de todos os casos. O rótulo é que some.",
                  icone: ICONES.apagar,
                  perigo: true,
                  aoTocar: aoApagar,
                },
              ]
            : []),
        ]}
      >
        <Icone de={ICONES.mais} tamanho={16} largura={1.9} />
      </MenuDeAcoes>
    </div>
  );
}

/**
 * O gerenciador. Abre por `?etiquetas=<id do caso>`.
 *
 * As etiquetas do caso vêm de dois lugares porque o caso pode não estar aberto:
 * da leitura do caso (`caso.tags`, um array) quando é o mesmo, e da LINHA da
 * mesa (`tags`, texto separado por `|`) quando o gerenciador foi aberto pelo
 * menu de contexto de outra conversa.
 */
export default function Etiquetas() {
  const { etiquetasDe, tagDaMesa, ajustar } = useNavegacao();
  const [texto, setTexto] = useState("");
  const alvo = etiquetasDe;
  /*
   * O QUE ETIQUETAR FAZ vem do gancho da casa (`useEtiquetasDoCaso`), e não de
   * uma cópia aqui.
   *
   * Esta caixa e o painel ancorado (`EtiquetasRapidas`) são duas superfícies
   * para o mesmo dado, e têm de concordar sobre qual é a lista do caso quando
   * ele não está aberto, o que se relê depois de gravar e por que tirar não
   * pergunta. Duas cópias dessa resposta é como uma delas envelhece — foi por
   * esse caminho que a etiqueta já ficou de fora do recorte da mesa uma vez.
   */
  const { nome, minhas, temSet, catalogo, cores, podeEditar, ocupado, mexer, alternar, escrever } = useEtiquetasDoCaso(alvo);
  const maior = catalogo.reduce((m, c) => Math.max(m, c.n ?? 0), 1);
  const busca = chaveDaEtiqueta(texto);
  const filtrado = busca ? catalogo.filter((c) => chaveDaEtiqueta(c.tag).includes(busca)) : catalogo;
  /* O que se digitou já existe? Enter põe a existente em vez de criar uma
     segunda com outra caixa — é o que a chave sem acento resolve. */
  const jaExiste = busca ? catalogo.some((c) => chaveDaEtiqueta(c.tag) === busca) : false;

  const fechar = () => ajustar({ etiquetas: null });

  /* Enter e o botão pela MESMA porta — a regra é do gancho (`oQueEnterFaz`). */
  const digitar = () => {
    escrever(texto);
    setTexto("");
  };

  const filtrar = (tag) => {
    /* Filtrar é ir para a MESA com o recorte — a caixa fecha junto, senão ela
       cobre justamente a lista que a pessoa pediu para ver. */
    ajustar({ tag: chaveDaEtiqueta(tagDaMesa) === chaveDaEtiqueta(tag) ? null : tag, etiquetas: null });
  };

  /* Caso que não está na mesa carregada nem é o aberto: sem id não há o que
     etiquetar, e a caixa aberta por link velho não pode escrever no vazio. */
  if (!alvo) return null;

  return (
    <Cortina
      aoFechar={fechar}
      largura="560px"
      titulo="Etiquetas"
      antesDoTitulo={<Icone de={ICONES.etiqueta} tamanho={18} largura={1.9} />}
      acima={`O recorte que o escritório inventa, em ${nome}. Etapa, tese e responsável já têm lugar próprio na mesa.`}
      rodape={
        <div className="flex flex-1 items-center justify-between gap-3">
          <span className="text-apoio text-muted-foreground">
            {minhas.length ? `${minhas.length} neste caso` : "nenhuma neste caso"} · {catalogo.length} no escritório
          </span>
          <Botao destaque rotulo="Pronto" aoTocar={fechar} />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <span className="mb-2 block text-selo font-bold tracking-[.08em] text-muted-foreground uppercase">Neste caso</span>
          {minhas.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {minhas.map((t) => (
                <NoCaso
                  key={t}
                  tag={t}
                  cor={cores.get(chaveDaEtiqueta(t))}
                  aoFiltrar={() => filtrar(t)}
                  /* guarda-ok: pergunta — tirar uma etiqueta DESTE caso é o gesto
                     mais repetido do dia e se desfaz num toque na lista abaixo.
                     Renomear e APAGAR, que valem para todos os casos, perguntam. */
                  aoTirar={podeEditar ? () => void mexer(chaveDaEtiqueta(t), () => api.tirarEtiqueta(alvo, t)) : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="m-0 rounded-xl border border-dashed border-foreground/14 p-[10px_12px] text-apoio leading-[1.5] text-muted-foreground">
              Nenhuma ainda. Marque uma da lista abaixo, ou escreva uma nova.
            </p>
          )}
        </div>

        <div>
          <span className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-selo font-bold tracking-[.08em] text-muted-foreground uppercase">Do escritório</span>
            <span className="text-apoio text-muted-foreground">o número é em quantos casos ela está</span>
          </span>

          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") return setTexto("");
              if (e.key !== "Enter") return;
              e.preventDefault();
              if (!podeEditar) return;
              digitar();
            }}
            placeholder={podeEditar ? "Procurar, ou escrever uma etiqueta nova…" : "Procurar uma etiqueta…"}
            aria-label={podeEditar ? "Procurar ou criar uma etiqueta" : "Procurar uma etiqueta"}
            className="mb-1.5 h-auto rounded-xl p-[9px_11px] text-corpo"
          />

          {podeEditar && texto.trim() && !jaExiste ? (
            <Botao
              destaque
              icone={ICONES.somar}
              rotulo={`Criar “${texto.trim()}” e pôr neste caso`}
              aoTocar={digitar}
              desabilitado={ocupado === busca}
              estilo="width:100%;justify-content:center;margin-bottom:6px"
            />
          ) : null}

          {filtrado.length ? (
            <div className="rounded-xl border border-border px-[12px] py-[2px]">
              {filtrado.map((c) => (
                <DoEscritorio
                  key={c.tag}
                  tag={c.tag}
                  cor={c.cor}
                  n={c.n ?? 0}
                  maior={maior}
                  tem={temSet.has(chaveDaEtiqueta(c.tag))}
                  ocupado={ocupado === chaveDaEtiqueta(c.tag)}
                  podeGerenciar={podeEditar}
                  aoAlternar={podeEditar ? () => alternar(c.tag) : undefined}
                  aoFiltrar={() => filtrar(c.tag)}
                  /* guarda-ok: pergunta — pintar vale para todos os casos, como
                     renomear, mas se desfaz num toque na mesma amostra (a
                     "automática" é a volta) e nada se perde. Perguntar aqui
                     seria pedágio numa escolha reversível. */
                  aoPintar={
                    podeEditar ? (cor) => void mexer(chaveDaEtiqueta(c.tag), () => api.pintarEtiqueta(c.tag, cor)) : undefined
                  }
                  aoRenomear={async () => {
                    const novo = await pedirTexto({
                      titulo: `Renomear “${c.tag}”`,
                      texto: `O rótulo muda nos ${c.n} caso(s) que o têm.`,
                      acao: "Renomear",
                      valor: c.tag,
                    });
                    if (novo) void mexer(chaveDaEtiqueta(c.tag), () => api.renomearEtiqueta(c.tag, novo));
                  }}
                  aoApagar={async () => {
                    if (
                      await confirmar({
                        titulo: `Apagar a etiqueta “${c.tag}”?`,
                        texto: `Ela sai dos ${c.n} caso(s) que a têm. Os casos ficam; o rótulo é que some.`,
                        acao: "Apagar a etiqueta",
                        perigo: true,
                      })
                    )
                      void mexer(chaveDaEtiqueta(c.tag), () => api.apagarEtiqueta(c.tag));
                  }}
                />
              ))}
            </div>
          ) : (
            /* Sem `acao`: o botão desta lista vazia é o campo logo acima, que
               já está na tela e com o texto digitado — um segundo botão faria a
               mesma coisa a 8px do primeiro. */
            <Vazio titulo={busca ? "Nenhuma com esse nome" : "O escritório ainda não tem etiquetas"}>
              {!podeEditar
                ? "Quem libera etiquetar é um sócio, em Equipe e acessos."
                : busca
                  ? "Aperte Enter no campo acima para criar esta e pôr no caso."
                  : "A primeira nasce escrevendo aqui em cima — não há cadastro em outra tela."}
            </Vazio>
          )}
        </div>
      </div>
    </Cortina>
  );
}

/* `buscaSem` fica importado de propósito no `useNavegacao` acima? Não: quem
   fecha usa `ajustar`, que já remove a chave. Mantido fora para não sugerir uma
   segunda forma de fechar a mesma caixa. */
void 0;
